// src/views/ProfessionalsManager.jsx
import React, { useState, useEffect } from 'react';
import { client } from '../utils/api';
import DataTable from '../components/DataTable';
import { swissTownData } from './../../data/swissTownData.js'; // Imported to mirror Registration logic

const CONTROLLER = 'professionalsController.php';

const columns = [
  { key: 'id', label: 'ID' },
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { 
    key: 'placeOfOperation', 
    label: 'Location (Canton/Town)', 
    render: (val) => `${val?.canton || ''} / ${val?.town || ''}` 
  },
];

const initialFormState = {
  email: '', firstName: '', lastName: '', telephone: '', website: '', employmentStatus: '', company: '', legalBusinessForm: '', professionServices: '', experienceInSpecialization: '',
  placeOfOperation: { canton: '', town: '' },
  languagesSpoken: { Russian: false, English: false, French: false, German: false, Other: false },
  motivationToJoin: { professionalContacts: false, projectBusinessPartner: false, careerOpportunities: false, clients: false, mentor: false, becomeMentor: false, becomeSpeaker: false, communityExpertise: false, likeMindedPeople: false, other: false },
  associationDataConsent: false
};

const employmentOptions =  [
    'Наёмный сотрудник / Employed',
    'Самозанятый / Предприниматель / Self-employed / Entrepreneur',
    'В поиске работы / Looking for a job',
    'Меняю сферу / Переквалификация / Career changer',
    'Студент / Student',
  ];

  const legalBusinessFormOptions =  [
    'Sole Proprietorship (e.g., Raison Individuelle / Einzelfirma)',
    'Partnership (e.g., Snc / Kollektivgesellschaft)',
    'Limited Liability Company (e.g., Sàrl / GmbH)',
    'Corporation (e.g., SA / AG)',
    'N/A',
  ];

  const experienceOptions =  [
    'Less Than 6 months',
    '6 months to 2 years',
    '2 years to 5 years',
    'More than 5 years',
  ];

export default function ProfessionalsManager({ onSendEmail = () => {} }) {
  const [professionals, setProfessionals] = useState([]);
  const [form, setForm] = useState(initialFormState);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmails, setSelectedEmails] = useState([]);

  useEffect(() => { loadProfessionals(); }, []);

  const loadProfessionals = async () => {
    try {
      const res = await client.get(CONTROLLER);
      setProfessionals(res.members || []);
    } catch (err) { setError('Failed to fetch professionals: ' + err.message); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await client.put(CONTROLLER, editingId, form);
      } else {
        await client.post(CONTROLLER, form);
      }
      setForm(initialFormState);
      setEditingId(null);
      loadProfessionals();
    } catch (err) { setError(err.message); }
  };

  const handleEdit = (prof) => {
    setEditingId(prof.id);
    setForm(prof);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this professional?')) return;
    try {
      await client.delete(CONTROLLER, id);
      loadProfessionals();
    } catch (err) { setError(err.message); }
  };

  const handleNestedCheck = (section, key, checked) => {
    setForm(prev => ({
      ...prev,
      [section]: { ...prev[section], [key]: checked }
    }));
  };

  // Helper variables to isolate specific sub-town lists matching Registration.js
  const selectedCanton = form.placeOfOperation.canton;
  const cantonData = swissTownData.find((item) => item.canton === selectedCanton);
  const townOptions = cantonData ? cantonData.towns : [];

  const filteredProfessionals = professionals.filter((professional) => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return true;

    const fullName = `${professional.firstName || ''} ${professional.lastName || ''}`.toLowerCase();
    const searchableText = [
      fullName,
      professional.email,
      professional.company,
      professional.professionServices,
      professional.placeOfOperation?.canton,
      professional.placeOfOperation?.town,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return searchableText.includes(query);
  });

  const toggleEmailSelection = (professional) => {
    const email = String(professional.email || '').trim();
    if (!email) return;

    setSelectedEmails((previous) => {
      if (previous.includes(email)) {
        return previous.filter((value) => value !== email);
      }
      return [...previous, email];
    });
  };

  const handleSendSelectedEmails = () => {
    if (!selectedEmails.length) {
      setError('Select at least one professional to send an email.');
      return;
    }

    setError('');
    onSendEmail({ recipients: selectedEmails, subject: '' });
  };

  return (
    <div className="admin-view-wrapper">
  {/* 1. Header Row Area */}
  <div className="admin-view-header">
    <h2 className="admin-title">Professional Registry Management</h2>
    {error && <div className="admin-error">{error}</div>}
  </div>

  {/* 2. Form Area (Spans full available width, handles inner grid fields) */}
  <form onSubmit={handleSubmit} className="admin-form-container" style={{ maxWidth: '100%' }}>
    <div className="grid-3-col">
      <div className="form-group">
        <label>First Name *</label>
        <input 
          type="text"
          placeholder="e.g. Elena" 
          required 
          value={form.firstName} 
          onChange={e => setForm({...form, firstName: e.target.value})} 
        />
      </div>

      <div className="form-group">
        <label>Last Name *</label>
        <input 
          type="text"
          placeholder="e.g. Petrova" 
          required 
          value={form.lastName} 
          onChange={e => setForm({...form, lastName: e.target.value})} 
        />
      </div>

      <div className="form-group">
        <label>Email *</label>
        <input 
          type="email" 
          placeholder="e.g. elena.p@association.ch" 
          required 
          value={form.email} 
          onChange={e => setForm({...form, email: e.target.value})} 
        />
      </div>

      <div className="form-group">
        <label>Telephone</label>
        <input 
          type="text"
          placeholder="e.g. +41 79 000 00 00" 
          value={form.telephone} 
          onChange={e => setForm({...form, telephone: e.target.value})} 
        />
      </div>

      <div className="form-group">
        <label>Website</label>
        <input 
          type="text"
          placeholder="e.g. https://..." 
          value={form.website} 
          onChange={e => setForm({...form, website: e.target.value})} 
        />
      </div>

      <div className="form-group">
        <label>Employment Status</label>
        <select 
          value={form.employmentStatus} 
          onChange={e => setForm({...form, employmentStatus: e.target.value})}
        >
          <option value="">Select Employment Status...</option>
          {employmentOptions.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Company Name</label>
        <input 
          type="text"
          placeholder="e.g. Architect Solutions LLC" 
          value={form.company} 
          onChange={e => setForm({...form, company: e.target.value})} 
        />
      </div>

      <div className="form-group">
        <label>Legal Form</label>
        <select 
          value={form.legalBusinessForm} 
          onChange={e => setForm({...form, legalBusinessForm: e.target.value})}
        >
          <option value="">Select Legal Business Form...</option>
          {legalBusinessFormOptions.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Years of Experience</label>
        <select 
          value={form.experienceInSpecialization} 
          onChange={e => setForm({...form, experienceInSpecialization: e.target.value})}
        >
          <option value="">Select Experience Level...</option>
          {experienceOptions.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>

      {/* Canton Input Select Dropdown */}
      <div className="form-group">
        <label>Canton</label>
        <select
          value={form.placeOfOperation.canton}
          onChange={e => setForm({
            ...form,
            placeOfOperation: { canton: e.target.value, town: '' }
          })}
        >
          <option value="">Choose a canton...</option>
          {swissTownData.map((item) => (
            <option key={item.canton} value={item.canton}>
              {item.canton}
            </option>
          ))}
        </select>
      </div>

      {/* Town Input Select Dropdown Dependent on Canton */}
      <div className="form-group">
        <label>Town</label>
        <select
          value={form.placeOfOperation.town}
          disabled={!selectedCanton}
          onChange={e => setForm({
            ...form,
            placeOfOperation: { ...form.placeOfOperation, town: e.target.value }
          })}
        >
          <option value="">
            {selectedCanton ? 'Choose a town...' : 'Select a canton first'}
          </option>
          {townOptions.map((town) => (
            <option key={town} value={town}>
              {town}
            </option>
          ))}
        </select>
      </div>
    </div>

    <div className="form-group" style={{ marginTop: '15px', width: '100%' }}>
      <label>Specialization Services</label>
      <textarea 
        placeholder="Describe technical specialization services provided, methodologies used, or focus sectors (Maximum 750 words)..." 
        rows={5}
        maxLength={4500}
        style={{ width: '100%', padding: '10px', boxSizing: 'border-box', resize: 'vertical' }}
        value={form.professionServices} 
        onChange={e => setForm({...form, professionServices: e.target.value})} 
      />
    </div>

    <div className="form-section-divider">
      <div>
        <h4 className="form-section-title">Languages Spoken</h4>
        {Object.keys(form.languagesSpoken).map(lang => (
          <label key={lang} className="form-checkbox-label">
            <input 
              type="checkbox" 
              checked={form.languagesSpoken[lang]} 
              onChange={e => handleNestedCheck('languagesSpoken', lang, e.target.checked)} 
            />&nbsp;
            {lang}<br />
          </label>
        ))}
      </div>
      <div>
        <h4 className="form-section-title">Motivations to Join</h4>
        <div className="checkbox-group-grid">
          {Object.keys(form.motivationToJoin).map(mot => (
            <label key={mot} className="form-checkbox-label">
              <input 
                type="checkbox" 
                checked={form.motivationToJoin[mot]} 
                onChange={e => handleNestedCheck('motivationToJoin', mot, e.target.checked)} 
              /> 
              &nbsp;
              {mot.replace(/([A-Z])/g, ' $1')}
            </label>
          ))}
        </div>
      </div>
    </div>

    <div className="form-footer">
      <label className="form-checkbox-label">
        <input 
          type="checkbox" 
          checked={form.associationDataConsent} 
          onChange={e => setForm({...form, associationDataConsent: e.target.checked})} 
        /> 
        Data Privacy Consent Given
      </label>
      <div className="btn-actions-container">
        {editingId && (
          <button 
            type="button" 
            onClick={() => { setEditingId(null); setForm(initialFormState); }} 
            className="btn btn-cancel"
          >
            Cancel
          </button>
        )}
        <button type="submit" className="btn btn-submit-professionals">
          {editingId ? 'Update Professional' : 'Register Professional'}
        </button>
      </div>
    </div>
  </form>

  {/* 3. Data Entry Table (Sits cleanly beneath the form element) */}
  <div className="admin-table-section">
    <div className="admin-table-toolbar">
      <div className="form-group" style={{ marginBottom: '0' }}>
        <label>Search professionals</label>
        <input
          type="text"
          placeholder="Search by name, email, company, town or canton"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <button
        type="button"
        className="btn btn-submit-professionals bulk-email-button"
        onClick={handleSendSelectedEmails}
        disabled={selectedEmails.length === 0}
      >
        Send Email to Selected
      </button>
    </div>
    <DataTable
      columns={columns}
      data={filteredProfessionals}
      onEdit={handleEdit}
      onDelete={handleDelete}
      emailSelection
      selectedEmails={selectedEmails}
      onToggleEmailSelection={toggleEmailSelection}
    />
  </div>
</div>
  );
}